import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const ValueSearchList = ({
  tableId,
  columnName,
  onSelect,
  className = '',
  selectedValue = '',
  autoFocus = false,
  isSearching = false,
  selectedIndex = -1,
  onItemsUpdate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [values, setValues] = useState([]);
  const [filteredValues, setFilteredValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [columnValueCount, setColumnValueCount] = useState(0);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const { allowedTables } = useAuth();
  const selectionInProgressRef = useRef(false);

  // Effect for initial data fetch
  useEffect(() => {
  const fetchValues = async () => {
  if (!tableId || !columnName) return;
  
  try {
    setLoading(true);
    setError(null);

    if (!allowedTables || !allowedTables[tableId]) {
      console.log('[ValueSearch] No access to table:', tableId);
      setError('Unauthorized access');
      setLoading(false);
      return;
    }
    
    const safeTableId = tableId.replace(/\./g, '_');
    const baseDocId = `${safeTableId}_${columnName}`;
    const mainDocRef = doc(db, 'index', baseDocId);
    const mainDocSnap = await getDoc(mainDocRef);
    
    if (mainDocSnap.exists()) {
      const data = mainDocSnap.data();
      
      // If this is a multi-chunk index
      if (data.isMultiChunk && data.chunks > 0) {
        console.log(`[ValueSearch] Loading multi-chunk index with ${data.chunks} chunks`);
        
        // Create array to hold all values from all chunks
        let allValues = [];
        
        // Fetch all chunk documents
        for (let i = 1; i <= data.chunks; i++) {
          console.log(`[ValueSearch] Loading chunk ${i} of ${data.chunks}`);
          const chunkDocRef = doc(db, 'index', `${baseDocId}_chunk_${i}`);
          const chunkSnap = await getDoc(chunkDocRef);
          
          if (chunkSnap.exists()) {
            const chunkData = chunkSnap.data();
            if (chunkData && Array.isArray(chunkData.values)) {
              allValues = [...allValues, ...chunkData.values];
              console.log(`[ValueSearch] Added ${chunkData.values.length} values from chunk ${i}, total now: ${allValues.length}`);
            }
          }
        }
        
        // Process values
        const uniqueValues = [...new Set(allValues.map(item => item.value))];
        console.log(`[ValueSearch] Final unique values count: ${uniqueValues.length}`);
        const formattedValues = uniqueValues.map(value => ({ value }));
        setValues(formattedValues);
        setFilteredValues(formattedValues);
        setColumnValueCount(uniqueValues.length); // Track the number of values
        if (onItemsUpdate) {
          onItemsUpdate(uniqueValues);
        }
      } else if (data && Array.isArray(data.values)) {
        // Handle single-document case
        const uniqueValues = [...new Set(data.values.map(item => item.value))];
        const formattedValues = uniqueValues.map(value => ({ value }));
        setValues(formattedValues);
        setFilteredValues(formattedValues);
        if (onItemsUpdate) {
          onItemsUpdate(uniqueValues);
        }
      } else {
        setError('No values found');
      }
    } else {
      setError('No values found');
    }
  } catch (err) {
    console.error('Error fetching values:', err);
    setError('Failed to load values');
    } finally {
      setLoading(false);
    }
  };

    fetchValues();
    
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      setIsOpen(true);
    }
  }, [tableId, columnName, autoFocus, allowedTables]);

  // Effect for filtering values
  useEffect(() => {
    if (!selectionInProgressRef.current) {
      // Check if this column has more than 10k values
      const requiresMinChars = columnValueCount > 10000;
      const searchTermTrimmed = searchTerm.trim();
      const meetsMinChars = searchTermTrimmed.length >= 3;
      
      // For large datasets that don't meet min chars requirement
      if (requiresMinChars && searchTermTrimmed && !meetsMinChars) {
        // Don't filter, but show a message by setting empty values
        setFilteredValues([]);
        return;
      }
      
      if (searchTermTrimmed) {
        if (!requiresMinChars || meetsMinChars) {
          const filtered = values.filter(item =>
            item.value.toLowerCase().includes(searchTermTrimmed.toLowerCase())
          );
          setFilteredValues(filtered);
          if (onItemsUpdate) {
            onItemsUpdate(filtered.map(item => item.value));
          }
        }
      } else {
        // For empty search term
        if (requiresMinChars) {
          // For large datasets, show limited or no preview when search is empty
          setFilteredValues([]); // Or use values.slice(0, 50) to show limited preview
        } else {
          // For small datasets, show all values
          setFilteredValues(values);
        }
        
        if (onItemsUpdate) {
          onItemsUpdate(values.map(item => item.value));
        }
      }
    }
  }, [searchTerm, values, columnValueCount]);

  // Effect for selected index from parent
  useEffect(() => {
    if (!selectionInProgressRef.current && selectedIndex !== highlightedIndex) {
      setHighlightedIndex(selectedIndex);
    }
  }, [selectedIndex]);

  // Effect for scroll into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  }, [highlightedIndex]);

  const handleSelect = (value) => {
    selectionInProgressRef.current = true;
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    onSelect(value);
    setTimeout(() => {
      selectionInProgressRef.current = false;
    }, 100);
  };

  const handleKeyDown = (e) => {
    if (loading) return;

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredValues.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : prev
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredValues.length) {
          handleSelect(filteredValues[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleInputChange = (e) => {
    if (!selectionInProgressRef.current) {
      const newValue = e.target.value;
      setSearchTerm(newValue);
      setIsOpen(true);
      if (!newValue) {
        onSelect('');
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={selectedValue || searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full bg-gray-700 text-white rounded-md px-4 py-2.5 focus:ring-2 focus:ring-secondary focus:outline-none"
          placeholder="Type to search values..."
        />
        <div className="absolute right-3 top-3">
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          ) : (
            <Search className="h-5 w-5 text-secondary" />
          )}
        </div>
      </div>

      {isOpen && !loading && !error && (
        <div className="absolute z-50 w-full mt-1 bg-gray-700 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
          {columnValueCount > 10000 ? (
            searchTerm.trim().length === 0 ? (
              <div className="px-4 py-2 text-gray-400">
                Large dataset ({columnValueCount.toLocaleString()} values). Type at least 3 characters to search.
              </div>
            ) : searchTerm.trim().length < 3 ? (
              <div className="px-4 py-2 text-gray-400">
                Type {3 - searchTerm.trim().length} more character{searchTerm.trim().length === 2 ? '' : 's'} to search
              </div>
            ) : filteredValues.length > 0 ? (
              <ul className="py-1" ref={listRef}>
                {filteredValues.map((item, index) => (
                  <li
                    key={index}
                    className={`px-4 py-2 cursor-pointer text-white ${
                      index === highlightedIndex ? 'bg-gray-800' : 'hover:bg-gray-800'
                    }`}
                    onClick={() => handleSelect(item.value)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {item.value}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-2 text-gray-400">No matching values found</div>
            )
          ) : filteredValues.length > 0 ? (
            <ul className="py-1" ref={listRef}>
              {filteredValues.map((item, index) => (
                <li
                  key={index}
                  className={`px-4 py-2 cursor-pointer text-white ${
                    index === highlightedIndex ? 'bg-gray-800' : 'hover:bg-gray-800'
                  }`}
                  onClick={() => handleSelect(item.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {item.value}
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-2 text-gray-400">No matching values found</div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-1 text-sm text-red-500">
          {error}
        </div>
      )}
    </div>
  );
};

export default ValueSearchList;